import { useState } from 'react';
import Button from '../../../components/Button';
import type { ParsedQuestion } from '../types/community-questions';
import { communityQuestionsService } from '../services/communityQuestionsService';
import { parseCsvFile } from '../utils/csvParser';

export const ImportQuestions = () => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [parsedQuestions, setParsedQuestions] = useState<ParsedQuestion[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setError('');
      setUploadStatus('');
      setParsedQuestions([]);
      setShowPreview(false);
    } else {
      setError('Please select a valid CSV file');
      setFile(null);
    }
  };

  const handlePreview = async () => {
    if (!file) return;

    setError('');
    setIsUploading(true);

    try {
      const csvContent = await file.text();
      const questions = parseCsvFile(csvContent);
      setParsedQuestions(questions);
      setShowPreview(true);
      setUploadStatus(`Parsed ${questions.length} questions successfully. Review and upload to database.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse CSV file');
      setParsedQuestions([]);
      setShowPreview(false);
    } finally {
      setIsUploading(false);
    }
  };

  const handleUpload = async () => {
    if (parsedQuestions.length === 0) return;

    setIsUploading(true);
    setError('');

    try {
      const uploadedCount = await communityQuestionsService.uploadQuestions(parsedQuestions);
      setUploadStatus(`Successfully uploaded ${uploadedCount} questions to the database!`);
      setParsedQuestions([]);
      setShowPreview(false);
      setFile(null);

      const fileInput = document.getElementById('csvFile') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload questions to database');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="p-4 space-y-6">
      {/* Upload Section */}
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Upload CSV File</h2>

        {/* File Format Info */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <h3 className="text-sm font-medium text-blue-900 mb-2">Required CSV Format:</h3>
          <p className="text-sm text-blue-700 mb-2">
            Pipe-separated file (|) with double-quoted strings:
          </p>
          <code className="text-xs text-blue-800 block bg-blue-100 p-2 rounded overflow-x-auto">
            "text"|"description"|"learning"|"type"|"opt1"|"opt1_isCorrect"|"opt2"|"opt2_isCorrect"|"opt3"|"opt3_isCorrect"|"opt4"|"opt4_isCorrect"
          </code>
          <ul className="text-sm text-blue-700 mt-2 space-y-1">
            <li>• type: "question" or "poll"</li>
            <li>• opt_isCorrect: "true" or "false" (for questions only)</li>
            <li>• At least 2 options required</li>
          </ul>
        </div>

        {/* File Input */}
        <div className="mb-4">
          <label htmlFor="csvFile" className="block text-sm font-medium text-gray-700 mb-2">
            Select CSV File
          </label>
          <input
            id="csvFile"
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4">
          <Button onClick={handlePreview} disabled={!file || isUploading}>
            Preview Questions
          </Button>
          {showPreview && (
            <Button onClick={handleUpload} disabled={parsedQuestions.length === 0 || isUploading} variant="primary">
              Upload to Database
            </Button>
          )}
        </div>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-700 break-words">{error}</p>
        </div>
      )}

      {uploadStatus && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-md">
          <p className="text-green-700">{uploadStatus}</p>
        </div>
      )}

      {/* Preview Section */}
      {showPreview && parsedQuestions.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Preview ({parsedQuestions.length} questions)
          </h2>

          <div className="space-y-4 max-h-96 overflow-y-auto">
            {parsedQuestions.map((question, index) => (
              <div key={index} className="border border-gray-200 rounded-md p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-gray-900">Question {index + 1}</h3>
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded ${question.type === 'question' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                      }`}
                  >
                    {question.type}
                  </span>
                </div>

                <p className="text-gray-900 mb-2 break-words">{question.text}</p>

                {question.learning && (
                  <p className="text-gray-600 text-sm mb-2">
                    <strong>Learning:</strong> {question.learning}
                  </p>
                )}

                <div className="mt-2">
                  <p className="text-sm font-medium text-gray-700 mb-1">Options:</p>
                  <ul className="space-y-1">
                    {Object.entries(question.options).map(([optionId, option]) => (
                      <li key={optionId} className="text-sm text-gray-600 flex items-center">
                        <span className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium mr-2">
                          {optionId}
                        </span>
                        {option.label}
                        {question.type === 'question' && option.isCorrect && (
                          <span className="ml-2 px-1 py-0.5 bg-green-100 text-green-800 text-xs rounded">Correct</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
